import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {BulkUploadChildDraft, IBulkUploadRow, IBulkUploadRowPatchRequest} from "../../model/IBulkUpload";
import {categoryApi} from "../../api/CategoriesService";
import BulkUploadRowItem from "./BulkUploadRowItem";
import {PlusIcon} from "@heroicons/react/24/outline";
import {TransactionType} from "../../model/TransactionType";

const formatAbsAmount = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const numeric = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
    if (Number.isNaN(numeric)) {
        return String(value).replace(/^-/, '');
    }
    return String(Math.abs(numeric));
};

const parseAmountInput = (value: string): number | null => {
    const cleaned = value.replace(',', '.').trim();
    if (!cleaned) {
        return null;
    }
    const numeric = Number(cleaned);
    return Number.isNaN(numeric) ? null : numeric;
};

const applyParentSign = (absValue: number, parentTotal: number): number => {
    return parentTotal < 0 ? -Math.abs(absValue) : Math.abs(absValue);
};

const buildDrafts = (children: IBulkUploadRow[]): Record<number, BulkUploadChildDraft> => {
    const drafts: Record<number, BulkUploadChildDraft> = {};
    children.forEach((child) => {
        drafts[child.id] = {
            total: formatAbsAmount(child.total ?? child.amount),
            categoryId: child.category ?? null,
            comment: child.comment ?? '',
            tagIds: (child.tags || []).map((tag) => tag.id),
        };
    });
    return drafts;
};

const BulkUploadRowGroup: React.FunctionComponent<{
    parent: IBulkUploadRow,
    childrenRows: IBulkUploadRow[],
    handleCategoryChange(event: any): void,
    selectedIds: Set<number>,
    accountCurrencySymbol: string | undefined,
    sessionAccountId?: number,
}> = ({
    parent,
    childrenRows,
    handleCategoryChange,
    selectedIds,
    accountCurrencySymbol,
    sessionAccountId,
}) => {
    const [drafts, setDrafts] = useState<Record<number, BulkUploadChildDraft>>(() => buildDrafts(childrenRows));
    const [patchBulkUploadRows] = categoryApi.usePatchBulkUploadRowsMutation();
    const [addChild] = categoryApi.useAddBulkUploadChildRowMutation();
    const [deleteChild] = categoryApi.useDeleteBulkUploadChildRowMutation();
    const [splitRow] = categoryApi.useSplitBulkUploadRowMutation();
    const [cancelUnite] = categoryApi.useCancelUniteBulkUploadRowMutation();

    useEffect(() => {
        setDrafts((prev) => {
            const next: Record<number, BulkUploadChildDraft> = {};
            childrenRows.forEach((child) => {
                if (prev[child.id]) {
                    // Keep local edits (especially totals) when children list changes
                    // e.g. after adding a new child row.
                    next[child.id] = prev[child.id];
                    return;
                }
                next[child.id] = {
                    total: formatAbsAmount(child.total ?? child.amount),
                    categoryId: child.category ?? null,
                    comment: child.comment ?? '',
                    tagIds: (child.tags || []).map((tag) => tag.id),
                };
            });
            return next;
        });
    }, [childrenRows]);

    const updateDraft = useCallback((childId: number, patch: Partial<BulkUploadChildDraft>) => {
        setDrafts((prev) => ({
            ...prev,
            [childId]: {
                ...prev[childId],
                ...patch,
            },
        }));
    }, []);

    const handleSplit = () => {
        splitRow(parent.id);
    };

    const handleAddChild = () => {
        if (parent.is_blocked) {
            return;
        }
        addChild(parent.id);
    };

    const handleDeleteChild = (childId: number) => {
        if (parent.is_blocked) {
            return;
        }
        deleteChild(childId);
    };

    const handleSplitParentSave = () => {
        const parentTotal = Number(parent.total);
        if (Number.isNaN(parentTotal)) {
            alert('Parent total is invalid');
            return;
        }

        let sum = 0;
        const payloads: IBulkUploadRowPatchRequest[] = [];

        for (const child of childrenRows) {
            const draft = drafts[child.id];
            if (!draft) {
                continue;
            }
            const parsed = parseAmountInput(draft.total);
            if (parsed === null) {
                alert('Each child row must have a valid Total / Block value');
                return;
            }
            if (Math.abs(parsed) <= 0) {
                alert('Each child row Total / Block must be greater than 0');
                return;
            }
            if (!draft.categoryId) {
                alert('Each child row must have a Category');
                return;
            }
            sum += Math.abs(parsed);
            const signedTotal = applyParentSign(parsed, parentTotal);
            payloads.push({
                id: child.id,
                total: signedTotal,
                // Backend mirrors total into amount for child rows.
                amount: signedTotal,
                category: draft.categoryId,
                category_type: TransactionType.Expense,
                comment: draft.comment ? draft.comment : null,
                tag_ids: draft.tagIds,
            });
        }

        if (Math.abs(sum - Math.abs(parentTotal)) > 0.001) {
            alert(
                `Sum of children Total (${sum.toFixed(2)}) must match parent Total (${Math.abs(parentTotal).toFixed(2)})`
            );
            return;
        }

        patchBulkUploadRows(payloads);
    };

    const handleSplitParentExclude = () => {
        const anyUploaded = childrenRows.some((child) => child.status === 'UPLOADED');
        const newStatus = anyUploaded ? 'EXCLUDED' : 'UPLOADED';
        patchBulkUploadRows(childrenRows.map((child) => ({id: child.id, status: newStatus})));
    };

    const handleCancelUnite = () => {
        cancelUnite(parent.id);
    };

    const isSplitParent = parent.status === 'SPLIT' || parent.status === 'SPLITED';
    const isUniteParent = parent.status === 'UNITE' || parent.status === 'UNITED';
    const canChangeChildrenCount = !parent.is_blocked && parent.status === 'SPLIT';
    const allChildrenHaveCategory = childrenRows.length > 0 && childrenRows.every((child) => {
        const draft = drafts[child.id];
        return !!(draft?.categoryId ?? child.category);
    });

    const childrenExcludeLabel = useMemo(() => {
        if (childrenRows.some((child) => child.status === 'UPLOADED')) {
            return 'Exclude';
        }
        return 'Restore';
    }, [childrenRows]);

    return (
        <>
            <BulkUploadRowItem
                row={parent}
                handleCategoryChange={handleCategoryChange}
                checked={selectedIds.has(parent.id)}
                accountCurrencySymbol={accountCurrencySymbol}
                sessionAccountId={sessionAccountId}
                rowMode={
                    isUniteParent
                        ? 'unite-parent'
                        : isSplitParent ? 'split-parent' : 'normal'
                }
                onSplit={handleSplit}
                onSplitParentSave={handleSplitParentSave}
                onSplitParentExclude={handleSplitParentExclude}
                onCancelUnite={handleCancelUnite}
                splitExcludeLabel={childrenExcludeLabel}
                allChildrenHaveCategory={allChildrenHaveCategory}
            />
            {childrenRows.map((child) => (
                <BulkUploadRowItem
                    key={child.id}
                    row={child}
                    handleCategoryChange={handleCategoryChange}
                    checked={selectedIds.has(child.id)}
                    accountCurrencySymbol={accountCurrencySymbol}
                    sessionAccountId={sessionAccountId}
                    rowMode={isUniteParent ? 'unite-child' : 'child'}
                    childDraft={drafts[child.id]}
                    onChildDraftChange={(patch) => updateDraft(child.id, patch)}
                    onDeleteChild={() => handleDeleteChild(child.id)}
                    canDeleteChild={canChangeChildrenCount}
                />
            ))}
            {canChangeChildrenCount && childrenRows.length > 0 && (
                <tr className="bg-gray-50">
                    <td colSpan={2} className="px-2 py-1.5"/>
                    <td colSpan={11} className="px-2 py-1.5">
                        <button
                            type="button"
                            onClick={handleAddChild}
                            className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-900"
                        >
                            <PlusIcon className="h-5 w-5"/>
                            <span>Add row</span>
                        </button>
                    </td>
                </tr>
            )}
        </>
    );
};

export default BulkUploadRowGroup;
